import { useEffect, useState } from 'react';
import { GroupedTrade } from '../../../lib/utils/tradeUtils';
import { getGroupedTradeMessages } from '../../../lib/api/tradeMessages';
import { useLeagueStore } from '../../../lib/leagueStore';
import { useUserStore } from '../../../lib/userStore';
import { GroupedTradeCard } from './GroupedTradeCard';
import { translate } from '../../../lib/translations';

interface TradeOffersProps {
  language: 'en' | 'fr';
}

export function TradeOffers({ language }: TradeOffersProps) {
  const [groupedTrades, setGroupedTrades] = useState<GroupedTrade[]>([]);
  const [page, setPage] = useState<number>(1);
  const pageSize = 2; // load 2 transactions per page to avoid statement timeouts
  const [loading, setLoading] = useState(true);
  const [totalGroups, setTotalGroups] = useState<number>(0);
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const currentUserId = useUserStore((state) => state.user_id);

  const loadTrades = async (requestedPage = 1) => {
    if (!selectedLeague?.id || !currentUserId) return;

    try {
      const { data, totalGroups, error } = await getGroupedTradeMessages(
        selectedLeague.id,
        Number(currentUserId),
        { page: requestedPage, pageSize }
      );

      if (error) throw error;

      // data is already grouped and paginated by groups
      setGroupedTrades(data || []);
      setPage(requestedPage);

      // store total groups so we can disable/enable next button
      setTotalGroups(totalGroups || 0);
    } catch (error) {
      console.error('Error loading trades:', error);
      // Removed toast error message as it was showing inappropriately
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // reset to first page when league or user changes
    setPage(1);
    setLoading(true);
    loadTrades(1);
  }, [selectedLeague, currentUserId, language]);

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{translate('loading', language)}</span>
        </div>
      </div>
    );
  }

  if (groupedTrades.length === 0) {
    return (
      <div className="text-center p-5">
        <p className="text-muted">{translate('no.trade.offers', language)}</p>
      </div>
    );
  }

  return (
    <div className="trade-offers">
      {groupedTrades.map((trade) => (
        <GroupedTradeCard
          key={`${trade.th_id}-${trade.time_stamp}`}
          trade={trade}
          currentUserId={Number(currentUserId)}
          onStatusUpdate={loadTrades}
          language={language}
        />
      ))}
      <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
        <button
          className="btn btn-secondary"
          onClick={() => {
            if (page > 1) {
              setLoading(true);
              loadTrades(page - 1);
            }
          }}
          disabled={page <= 1}
        >
          {translate('prev', language)}
        </button>

        <span className="mx-2">{translate('page', language)} {page}</span>

        <button
          className="btn btn-secondary"
          onClick={() => {
            setLoading(true);
            loadTrades(page + 1);
          }}
          disabled={page >= Math.ceil((totalGroups || 0) / pageSize)}
        >
          {translate('next', language)}
        </button>
      </div>
    </div>
  );
}